import React, { useEffect, useRef, useState } from "react";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import TechnologyUsed from "./TechnologyUsed";
import TechnologyUsedCategories from "./TechnologyUsedCategories";
import NoInfo from "../NoInfo";
import ScrollSpy from "react-ui-scrollspy";
import LoaderInfinite from "./LoaderInfinite";
import { useMutation } from "react-query";
import { Cross } from "lucide-react";
import Input from "../../Tags/Input";
import { getTechnography } from "../../../apis/ext";
import Image from "../../Tags/Image";

export default function TechStack({ organizationId = "", workspaceId = "", company = {} }) {
  const [technologies, setTechnologies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState("all-tech");
  const [currentSubCategoryId, setCurrentSubCategoryId] = useState("");
  const [currentCategoryItems, setCurrentCategoryItems] = useState([]);

  const getItemsByCategory = (data, category, sub = false) => {
    let arr = [];
    if (sub) {
      arr = data.filter((item) => item.categoryIds.some((id) => category === id));
      setCurrentSubCategoryId(category);
    } else {
      arr = data.filter((item) =>
        item.categoryIds.some((id) => category?.children?.some((el) => el.id === id))
      );
      setCurrentSubCategoryId("");
    }

    setCurrentCategoryItems(arr);
    // return arr;
  };

  const findCategoriesByIds = (ids) => {
    let result = [];
    categories.forEach((category) => {
      const matchingChildren = category.children.filter((child) => ids.includes(child.id));
      result = result.concat(matchingChildren);
    });

    return result;
  };

  const [pollingType, setPollingType] = useState(null); // "404" | "pending" | null
  const pollingIntervalRef = useRef(null);

  const getTechnographyFn = useMutation("get_technography", getTechnography, {
    onSuccess(data, variables, context) {
      if (data && data.data) {
        const technologiesArr = data.data?.products;
        setTechnologies(technologiesArr);
        setCategories(data.data?.categories);
        if (data.data?.categories && data.data?.categories.length > 0) {
          setCurrentCategory("all-tech");
          setFilteredTechnologies(technologiesArr);
          // setCurrentCategory(formatString(data.data?.categories[0]));
          // getItemsByCategory(technologiesArr, data.data?.categories[0]);

          // ✅ Check for pending enrichment task
          if (data.data.hasPendingEnrichmentTask) {
            setPollingType("pending");
          } else {
            setPollingType(null);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        }
      }
    },
    onError(error) {
      if (error?.status === 404) {
        setPollingType("404");
      }
    },
  });
  const handleGetTechnography = () => {
    // organization_id: "bOX5iJUB1ejGaXifcoUl",
    getTechnographyFn.mutate({
      organization_id: organizationId,
      options: {
        headers: { "X-Workspace-Id": workspaceId },
      },
    });
  };
  useEffect(() => {
    if (workspaceId && organizationId) {
      handleGetTechnography();
    }
  }, [workspaceId, organizationId]);

  // ✅ Handle polling behavior
  useEffect(() => {
    if (pollingType && !pollingIntervalRef.current) {
      const intervalTime = pollingType === "404" ? 10000 : 5000;

      pollingIntervalRef.current = setInterval(() => {
        console.log(`Polling getTechnography (${pollingType})...`);
        handleGetTechnography();
      }, intervalTime);
    }

    if (!pollingType && pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [pollingType]);

  const formatString = (obj) => {
    if (!obj) {
      return "";
    }
    if (typeof obj === "number") {
      return obj;
    }
    const input = obj?.name;
    if (typeof input === "number") {
      return input;
    }
    if (typeof input !== "string") {
      throw new Error("Input must be a string");
    }

    return input
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "-");
  };

  const scrollToCategory = (id) => {
    const element = document.getElementById(id);

    if (element) {
      const offset = 70;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const [searchQuery, setSearchQuery] = useState(""); // Store search input
  const [filteredTechnologies, setFilteredTechnologies] = useState(technologies);

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    // Filter technologies based on the query
    const filtered = technologies.filter((tech) => {
      return tech?.name.toLowerCase().includes(query) || tech?.vendor?.name.toLowerCase().includes(query);
    });

    setFilteredTechnologies(filtered);
  };

  useEffect(() => {
    setFilteredTechnologies(technologies);
  }, [technologies.length]);

  if (getTechnographyFn.isLoading && categories.length === 0) {
    return <LoaderInfinite />;
  }

  if (company && company.id && technologies.length === 0 && !getTechnographyFn.isLoading) {
    return (
      <div>
        {company && company.technology_used && company.technology_used.length > 0 && (
          <TechnologyUsed data={company.technology_used} />
        )}

        {company && company.technology_used_categories && company.technology_used_categories.length > 0 && (
          <TechnologyUsedCategories data={company.technology_used_categories} />
        )}
        {company && !company.technology_used && !company.technology_used_categories && <LoaderInfinite />}
      </div>
    );
  }

  const removeActiveScrollSpy = () => {
    // Select all elements that have both classes
    const activeItems = document.querySelectorAll(".tech_stack_left_bar_item.active-scroll-spy");

    // Remove 'active-scroll-spy' class from each of them
    if (activeItems && activeItems.length > 0) {
      activeItems.forEach((item) => {
        item.classList.remove("active-scroll-spy");
      });
    }
  };

  const ReturnRow = ({ tech }) => {
    if (tech) {
      return (
        <div className="inline-flex items-center space-x-4">
          <span>
            <Image
              src={tech?.logo_url || "/images/ext/no-img.webp"}
              width={50}
              alt="Img"
              className="rounded-sm no-invert"
              nobaseurl={tech?.logo_url ? true : false}
            />
          </span>
          <div>
            <div className="flex w-full">
              <h4 class="text-gray-900 font-bold text-[14px] whitespace-pre">{tech?.name}</h4>
              <span className="ml-2 -mt-1">
                {tech.categoryIds &&
                  tech.categoryIds.length > 0 &&
                  findCategoriesByIds(tech.categoryIds) &&
                  findCategoriesByIds(tech.categoryIds).length > 0 &&
                  findCategoriesByIds(tech.categoryIds).map((el) => (
                    <span className="inline-block text-[11px] bg-gray-dark-25 text-gray-600 border border-border px-2 py-[1px] rounded-[20px] mr-2 mb-1">
                      {el?.name}
                    </span>
                  ))}
              </span>
            </div>
            <h4 class=" text-gray-600 mt-0.5 text-xs">{tech?.vendor?.name}</h4>
          </div>
        </div>
      );
    }
  };

  return (
    <div>
      {getTechnographyFn.isLoading ? (
        <div className="bg-background border border-border rounded-md p-3 flex items-center space-x-3 text-sm text-gray-700">
          <div className="loader loader_circle inline-block"></div>
          <span>Refreshing data...</span>
        </div>
      ) : (
        <Input
          placeholder="Search for keyword or company"
          className="h-11 bg-background"
          icon={<MagnifyingGlassIcon width={20} height={20} className="text-gray-500" />}
          value={searchQuery}
          onChange={handleSearch}
        />
      )}
      <div className={`flex ${searchQuery ? "" : "space-x-4"} mt-4`}>
        <div className={`w-[250px] block ${searchQuery ? "hidden" : ""}`}>
          <div className="tech_stack_left_bar">
            <span
              onClick={() => {
                setCurrentCategory("all-tech");
                setFilteredTechnologies(technologies);
              }}
              className={`w-full block text-left py-2 px-3 rounded-md text-sm font-bold tech_stack_left_bar_item cursor-pointer hover:bg-background hover:border-gray-200 ${
                currentCategory === "all-tech" && "active-scroll-spy"
              }`}
            >
              All Technologies
            </span>
            {categories &&
              categories.length > 0 &&
              categories.map((category) => (
                <>
                  <span
                    onClick={() => {
                      currentCategory !== formatString(category) &&
                        setCurrentCategory(formatString(category));

                      getItemsByCategory(filteredTechnologies, category);
                    }}
                    className={`w-full block text-left py-2 px-3 rounded-md text-sm font-bold tech_stack_left_bar_item cursor-pointer hover:bg-background hover:border-gray-200 ${
                      currentCategory === formatString(category) && "active-scroll-spy"
                    }`}
                    data-to-scrollspy-id={formatString(category)}
                  >
                    {category?.name}
                  </span>
                  {currentCategory === formatString(category) && (
                    <div className="pl-3 pt-3">
                      {category &&
                        category.children &&
                        category.children.length > 0 &&
                        category.children.map((item) => (
                          <span
                            className={`relative border-l w-full flex items-center justify-between text-left py-2 px-3 rounded-r text-sm font-semibold  cursor-pointer 
                              ${
                                currentSubCategoryId === item.id
                                  ? "border-brand-600 border-l-2 text-brand-700"
                                  : "hover:bg-background hover:border-gray-200 text-gray-dark-600"
                              }
                              `}
                            onClick={() => {
                              getItemsByCategory(filteredTechnologies, item.id, true);
                            }}
                          >
                            <span>{item?.name}</span>
                            {currentSubCategoryId === item.id && (
                              <Image
                                className="cursor-pointer opacity-45  no-invert"
                                src="/images/ext/cross.svg"
                                alt="Cancel"
                                width={10}
                                height={10}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  getItemsByCategory(filteredTechnologies, category);
                                }}
                              />
                            )}
                          </span>
                        ))}
                    </div>
                  )}
                </>
              ))}
          </div>
        </div>
        <div className="flex-1">
          {categories && categories.length > 0 && (
            <div>
              {!searchQuery && categories && currentCategory === "all-tech" && (
                <div>
                  {filteredTechnologies &&
                    filteredTechnologies.length > 0 &&
                    filteredTechnologies.map((tech) => (
                      <div
                        className={`flex py-3 space-x-4 px-3 items-center justify-between border rounded-[8px] overflow-hidden  bg-background  relative mb-2`}
                      >
                        <ReturnRow tech={tech} />
                      </div>
                    ))}
                </div>
              )}
              {!searchQuery &&
                categories &&
                currentCategory !== "all-tech" &&
                categories.length > 0 &&
                categories.map((category, index) => (
                  <div key={formatString(category)} id={formatString(category)}>
                    {currentCategory === formatString(category) &&
                      currentCategoryItems &&
                      currentCategoryItems.length > 0 &&
                      currentCategoryItems.map((tech) => (
                        <div
                          className={`flex py-3 space-x-4 px-3 items-center justify-between border rounded-[8px] overflow-hidden    relative mb-2 
                            ${
                              currentCategory === formatString(category)
                                ? "border-border bg-background"
                                : "border-border bg-background"
                            }
                          `}
                        >
                          <ReturnRow tech={tech} />
                        </div>
                      ))}
                  </div>
                ))}
              {searchQuery && (
                <div>
                  {filteredTechnologies &&
                    filteredTechnologies.length > 0 &&
                    filteredTechnologies.map((tech) => (
                      <div
                        className={`flex py-3 space-x-4 px-3 items-center justify-between border rounded-[8px] overflow-hidden  bg-background  relative mb-2`}
                      >
                        <ReturnRow tech={tech} />
                      </div>
                    ))}
                </div>
              )}
              {technologies &&
                technologies.length > 0 &&
                filteredTechnologies &&
                filteredTechnologies.length === 0 && <NoInfo heading="No result found!" />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
